import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { PureComputed, Subscribable } from 'knockout';
import { CollectionDataSource } from 'managers/datasources/collection';
import events from 'managers/events';
import IdeationManager from 'managers/ideation';
import { IWCCStorageManager } from 'managers/iStorage';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import ThreadVote, { JSONThreadVote } from 'models/threadVote';
import { TopicsService } from 'services/topics';

export interface TopicVotesManagerConfig {
    topicId: string
}

@injectable()
export default class TopicVotesManager {
    private topicId: string
    private source: CollectionDataSource<ThreadVote>

    votes: Subscribable<Array<ThreadVote>>

    loading: PureComputed<boolean>
    updating: PureComputed<boolean>

    constructor(
        @inject(wccModules.managerConfig) { topicId }: TopicVotesManagerConfig,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.topicsService) private topicsService: TopicsService,
        @inject(wccModules.effects) effects: EffectsContainer,
    ) {
        this.topicId = topicId;

        this.source = effects.register(new CollectionDataSource({
            load: this.load.bind(this),
            mapper: jsonVote => new ThreadVote(jsonVote)
        }));

        this.votes = this.source.list;        

        this.loading = ko.pureComputed(() => this.source.busy() && this.source.requestsProcessed() === 0);
        this.updating = ko.pureComputed(() => this.source.busy() && this.source.requestsProcessed() > 0);

        const ideationManager = storage.get(IdeationManager, { topicId: topicId });
        const isVotingStage = ideationManager.pluck(m => m.isVotingStage, false);

        //we're inside voting stage so any replies changes require votes update
        effects.register(isVotingStage => {
            if (isVotingStage) {
                return withEffect([
                    events.replyAdded.on(this.onLocalReplyAdded.bind(this)),
                    events.replyUpdated.on(this.onLocalReplyUpdated.bind(this)),
                    events.replyDeleted.on(this.onLocalReplyDeleted.bind(this))
                ]);
            }
        }, [isVotingStage]);
    }

    update() {
        this.source.load();
    }

    private load() {
        return this.topicsService.queries.votes(this.topicId).toArray();
    }

    private onLocalReplyAdded(topicId: string) {
        if (this.topicId == topicId)
            this.update();
    }

    private onLocalReplyUpdated(topicId: string) {
        if (this.topicId == topicId)
            this.update();
    }

    private onLocalReplyDeleted(topicId: string) {
        if (this.topicId == topicId)
            this.update();
    }
}